import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, assetUrl } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useLocale } from '../contexts/LocaleContext.jsx';

const FEATURED_LANGUAGES = ['', 'Thai', 'English', 'Chinese', 'Japanese', 'Korean'];

export default function Caddies() {
  const { user } = useAuth();
  const { t } = useLocale();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [golfCourse, setGolfCourse] = useState('');
  const [language, setLanguage] = useState('');
  const [courses, setCourses] = useState([]);
  const [caddies, setCaddies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    api.caddies.courses()
      .then((data) => setCourses(data.golf_courses ?? []))
      .catch(() => setCourses([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    api.caddies.list({
      page: 1,
      ...(query ? { search: query } : {}),
      ...(golfCourse ? { golf_course: golfCourse } : {}),
      ...(language ? { language } : {}),
    })
      .then((data) => setCaddies(data.caddies ?? []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [query, golfCourse, language]);

  async function handleFollow(event, id) {
    event.preventDefault();
    event.stopPropagation();
    try {
      const { following } = await api.caddies.toggleFollow(id);
      setCaddies((current) => current
        .map((caddie) => caddie.id === id ? { ...caddie, is_following: following } : caddie)
        .sort((a, b) => Number(b.is_following) - Number(a.is_following) || a.display_name.localeCompare(b.display_name)));
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">{t('findCaddie')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('findCaddieHint')}</p>
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-3">
        <input
          className="input"
          placeholder={t('searchCaddies')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select className="input" value={golfCourse} onChange={(event) => setGolfCourse(event.target.value)}>
          <option value="">{t('allGolfCourses')}</option>
          {courses.map((course) => <option key={course} value={course}>{course}</option>)}
        </select>
        <div>
          <input
            className="input"
            list="caddie-language-options"
            placeholder={t('allLanguages')}
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
          />
          <datalist id="caddie-language-options">
            {FEATURED_LANGUAGES.filter(Boolean).map((item) => <option key={item} value={item} />)}
          </datalist>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : caddies.length === 0 ? (
        <div className="card p-12 text-center text-gray-400">
          <p className="font-medium">{t('noCaddies')}</p>
          <p className="mt-1 text-sm">{t('tryDifferent')}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {caddies.map((caddie) => (
            <article key={caddie.id} className="card p-5 hover:shadow-md transition-shadow">
              <Link to={`/caddies/${caddie.id}`} className="block">
                <div className="flex items-center gap-3">
                  {caddie.avatar_url ? (
                    <img src={assetUrl(caddie.avatar_url)} alt={caddie.display_name} className="w-14 h-14 rounded-full object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-xl font-bold">
                      {caddie.display_name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{caddie.display_name}</p>
                    <p className="text-sm text-brand-700 truncate">{caddie.workplace || t('golfCourseNotSet')}</p>
                  </div>
                </div>
                {caddie.languages?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {caddie.languages.map((item) => (
                      <span key={item} className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                        {item}
                      </span>
                    ))}
                  </div>
                )}
                {caddie.bio && <p className="mt-3 text-sm text-gray-500 line-clamp-2">{caddie.bio}</p>}
              </Link>
              {user?.role === 'golfer' && (
                <button
                  type="button"
                  onClick={(event) => handleFollow(event, caddie.id)}
                  className="btn-secondary mt-4 w-full text-sm"
                >
                  {caddie.is_following ? t('following') : t('follow')}
                </button>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
